import { Injectable, signal, computed } from '@angular/core';
import { InvestigationTypeEntity } from '../../domain/entities/investigation-type.entity';
import { InvestigationTypeDetailEntity } from '../../domain/entities/investigation-type-detail.entity';
import { InvestigationTypeCategoryEntity } from '../../domain/entities/investigation-type-category.entity';
import { InvestigationTypeDeletionImpactEntity } from '../../domain/entities/investigation-type-deletion-impact.entity';

@Injectable({ providedIn: 'root' })
export class InvestigationTypeState {
  private readonly _investigationTypes = signal<InvestigationTypeEntity[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _currentInvestigationType = signal<InvestigationTypeDetailEntity | null>(null);
  private readonly _saving = signal(false);
  private readonly _categories = signal<InvestigationTypeCategoryEntity[]>([]);
  private readonly _savingInvestigationTypeStatus = signal(false);
  private readonly _isDeletingInvestigationType = signal(false);
  private readonly _loadingChecklist = signal(false);
  private readonly _errorChecklist = signal<string | null>(null);
  private readonly _savingChecklist = signal(false);
  private readonly _investigationTypesCount = signal<number>(0);
  private readonly _investigationTypeDeletionImpact = signal<InvestigationTypeDeletionImpactEntity | null>(null);
  private readonly _loadingInvestigationTypeDeletionImpact = signal(false);
  private readonly _errorInvestigationTypeDeletionImpact = signal<string | null>(null);

  // Read-only signals
  readonly investigationTypes = this._investigationTypes.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly currentInvestigationType = this._currentInvestigationType.asReadonly();
  readonly saving = this._saving.asReadonly();
  readonly categories = this._categories.asReadonly();
  readonly savingInvestigationTypeStatus = this._savingInvestigationTypeStatus.asReadonly();
  readonly isDeletingInvestigationType = this._isDeletingInvestigationType.asReadonly();
  readonly loadingChecklist = this._loadingChecklist.asReadonly();
  readonly errorChecklist = this._errorChecklist.asReadonly();
  readonly savingChecklist = this._savingChecklist.asReadonly();
  readonly investigationTypesCount = this._investigationTypesCount.asReadonly();
  readonly investigationTypeDeletionImpact = this._investigationTypeDeletionImpact.asReadonly();
  readonly loadingInvestigationTypeDeletionImpact = this._loadingInvestigationTypeDeletionImpact.asReadonly();
  readonly errorInvestigationTypeDeletionImpact = this._errorInvestigationTypeDeletionImpact.asReadonly();
  readonly hasInvestigationTypes = computed(() => this._investigationTypes().length > 0);
  readonly activeInvestigationTypes = computed(() =>
    this._investigationTypes().filter((it) => it.isActive),
  );

  setInvestigationTypes(investigationTypes: InvestigationTypeEntity[]): void {
    this._investigationTypes.set(investigationTypes);
  }

  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  setError(error: string | null): void {
    this._error.set(error);
    this._loading.set(false);
  }

  setCurrentInvestigationType(investigationType: InvestigationTypeDetailEntity | null): void {
    this._currentInvestigationType.set(investigationType);
  }

  setSaving(saving: boolean): void {
    this._saving.set(saving);
  }

  setLoadingChecklist(loading: boolean): void {
    this._loadingChecklist.set(loading);
  }

  setErrorChecklist(error: string | null): void {
    this._errorChecklist.set(error);
  }

  clearErrorChecklist(): void {
    this._errorChecklist.set(null);
  }

  setSavingChecklist(saving: boolean): void {
    this._savingChecklist.set(saving);
  }

  setCategories(categories: InvestigationTypeCategoryEntity[]): void {
    this._categories.set(categories);
  }

  addInvestigationType(investigationType: InvestigationTypeEntity): void {
    this._investigationTypes.update((current) => [...current, investigationType]);
  }

  updateInvestigationType(updated: InvestigationTypeEntity): void {
    this._investigationTypes.update((current) =>
      current.map((it) => (it.id === updated.id ? updated : it)),
    );
  }

  removeInvestigationType(id: string): void {
    this._investigationTypes.update((current) => current.filter((it) => it.id !== id));
  }

  setSavingInvestigationTypeStatus(saving: boolean): void {
    this._savingInvestigationTypeStatus.set(saving);
  }

  setDeletingInvestigationType(deleting: boolean): void {
    this._isDeletingInvestigationType.set(deleting);
  }

  clearCurrentInvestigationType(): void {
    this._currentInvestigationType.set(null);
  }

  clearError(): void {
    this._error.set(null);
  }

  setInvestigationTypesCount(count: number): void {
    this._investigationTypesCount.set(count);
  }

  setInvestigationTypeDeletionImpact(impact: InvestigationTypeDeletionImpactEntity | null): void {
    this._investigationTypeDeletionImpact.set(impact);
  }

  setLoadingInvestigationTypeDeletionImpact(loading: boolean): void {
    this._loadingInvestigationTypeDeletionImpact.set(loading);
  }

  setErrorInvestigationTypeDeletionImpact(error: string | null): void {
    this._errorInvestigationTypeDeletionImpact.set(error);
  }

  clear(): void {
    this._investigationTypes.set([]);
    this._loading.set(false);
    this._error.set(null);
    this._currentInvestigationType.set(null);
    this._saving.set(false);
    this._categories.set([]);
    this._loadingChecklist.set(false);
    this._errorChecklist.set(null);
    this._savingChecklist.set(false);
    this._investigationTypeDeletionImpact.set(null);
    this._loadingInvestigationTypeDeletionImpact.set(false);
    this._errorInvestigationTypeDeletionImpact.set(null);
  }
}
